import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

const routes = [
  {
    key: "a",
    label: "Route A",
    naam: "Lekker Leven",
    kern: "Warm, aards, dóende mensen. Serifs die ademen, koraal & grasgroen, zon in het beeld.",
    gevoel: "Het leven mag hier gewoon weer leuk zijn.",
    kleuren: ["#E06B3F", "#78A85A", "#F0B98D", "#FFF6E8"],
    bg: "#FFF6E8",
    fg: "#1F2A22",
    accent: "#E06B3F",
    img: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  {
    key: "b",
    label: "Route B",
    naam: "De Volgende Beweging",
    kern: "Editorial, volwassen, magazine-achtig. Nachtblauw als drager, koraal als vonk.",
    gevoel: "Serieus genomen worden. Klaar voor de volgende stap.",
    kleuren: ["#1F5132", "#E06B3F", "#E9E4DA", "#0F1A2A"],
    bg: "#0F1A2A",
    fg: "#F5F1E8",
    accent: "#E06B3F",
    img: "https://images.unsplash.com/photo-1519741347686-c1e0aadf4611?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  {
    key: "c",
    label: "Route C",
    naam: "Speelruimte",
    kern: "Speels, energiek, kronkels als motief. Lichtblauw met geel & koraal — jong maar volwassen.",
    gevoel: "Er mag weer iets ontstaan.",
    kleuren: ["#97B79D", "#F0B98D", "#E06B3F", "#FAF7F1"],
    bg: "#FAF7F1",
    fg: "#1F5132",
    accent: "#97B79D",
    img: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
];

export default function Moodboards() {
  usePageMeta({
    title: "Merkrichtingen · LevelLeven",
    description: "Drie moodboards om op gevoel én positionering te kiezen.",
  });

  return (
    <div className="bg-[#FAF7F1] min-h-screen" data-testid="moodboard-index">
      <section className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pt-24 pb-16">
        <p className="text-xs uppercase tracking-[0.35em] text-[#E06B3F]">Interne review · Merkrichting</p>
        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-[#1D2A22] font-light mt-6 max-w-4xl">
          Drie gevoelens.
          <span className="italic text-[#E06B3F]"> Eén LevelLeven.</span>
        </h1>
        <p className="mt-8 max-w-2xl text-lg text-[#4A5D4E] leading-relaxed">
          Ontwerpvraag: <span className="italic">"Zou iemand die al lang vastzit hier zin van krijgen om weer een stap te zetten?"</span>
          Kies op gevoel — dan pas op systeem.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pb-32">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {routes.map((r, i) => (
            <motion.div
              key={r.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <Link
                to={`/moodboard/${r.key}`}
                data-testid={`moodboard-card-${r.key}`}
                className="group block rounded-3xl overflow-hidden border border-[#E5E1D8] hover:shadow-2xl transition-shadow"
                style={{ background: r.bg, color: r.fg }}
              >
                <div className="aspect-[4/5] relative overflow-hidden">
                  <img
                    src={r.img}
                    alt={r.naam}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                  />
                  <div className="absolute top-4 left-4 flex gap-2">
                    {r.kleuren.map((c) => (
                      <span key={c} className="w-6 h-6 rounded-full border border-white/50" style={{ background: c }} />
                    ))}
                  </div>
                  <div className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.3em] bg-white/90 text-[#1D2A22] px-3 py-1 rounded-full">
                    {r.label}
                  </div>
                </div>
                <div className="p-8">
                  <h2 className="font-heading text-3xl leading-tight">{r.naam}</h2>
                  <p className="mt-3 italic text-lg opacity-90">"{r.gevoel}"</p>
                  <p className="mt-4 text-sm opacity-80 leading-relaxed">{r.kern}</p>
                  <span className="mt-8 inline-flex items-center gap-2 text-sm font-medium border-b pb-1 group-hover:gap-4 transition-all" style={{ borderColor: r.accent }}>
                    Bekijk homepage-hero
                    <ArrowUpRight size={16} />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
